import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, X } from "lucide-react";
import { API } from "@/lib/api";

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors";

export default function ConsultationModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", comment: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/applications`, {
        name: form.name.trim() || "კონსულტაცია",
        phone: form.phone.trim(),
        vehicle: "",
        comment: form.comment.trim() ? `ზარის მოთხოვნა: ${form.comment.trim()}` : "ზარის მოთხოვნა",
      });
      toast.success("მოთხოვნა მიღებულია — მალე დაგირეკავთ");
      setForm({ name: "", phone: "", comment: "" });
      onClose();
    } catch {
      toast.error("გაგზავნა ვერ მოხერხდა — სცადე ხელახლა");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={onClose}
          data-testid="consultation-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md card-lux p-7"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#D4AF37]" /> ზარის მოთხოვნა
                </h3>
                <p className="text-sm text-gray-400 mt-1.5">დაგვიტოვე ნომერი — ჩვენ დაგირეკავთ და გაგიცავით კონსულტაციას.</p>
              </div>
              <button onClick={onClose} data-testid="consultation-close" className="text-gray-500 hover:text-white transition-colors p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">სახელი</label>
                <input
                  data-testid="consultation-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                  placeholder="შენი სახელი"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">ტელეფონის ნომერი <span className="text-[#D4AF37]">*</span></label>
                <input
                  data-testid="consultation-phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputCls}
                  placeholder="5XX XX XX XX"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">კომენტარი</label>
                <textarea
                  data-testid="consultation-comment"
                  rows={3}
                  value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  className={`${inputCls} resize-none`}
                  placeholder="მაგ. მაინტერესებს Prius 2018, როდის გათავისუფლდება?"
                />
              </div>
              <button type="submit" disabled={sending} data-testid="consultation-submit" className="btn-gold w-full text-base !py-4 disabled:opacity-60">
                {sending ? "იგზავნება..." : "მოითხოვე ზარი"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
